import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Slide,
    Divider,
    Typography, 
    CircularProgress
} from '@mui/material';
import {PrimaryButton, SecondaryButton, SubTitleScreen, IconContainer, DialogTitleText} from "../SystemDesign/styles";
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import theme from '../theme';
import '../styles/nestedDialog.css';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MuiDialog = ({
                       icon,
                       triggerButton,
                       title,
                       subtitle,
                       content,
                       existAction,
                       actionTitle,
                       actionFunction,
                       onClose,
                       isLoading,
                       actionType,
                       formId,
                       maxWidth,
                       disableAction,
                       open: externalOpen,
                       onExternalClose,
                       children,
                       nested = false,
                       sx: customSx,
                       ...otherProps
                   }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(externalOpen || false);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = (event, reason) => {
        // Si le clique provient en dehors du DIALOG, alors on l'ignore
        if (reason && reason == "backdropClick")
            return;
        setOpen(false);
        // Call external onClose handler if provided
        if (onExternalClose) {
            onExternalClose();
        }
    };

    useEffect(() => {
        if (onClose) {
            handleClose();
        }
    }, [onClose]);
    
    // Sync with external open state if provided
    useEffect(() => {
        if (externalOpen !== undefined) {
            setOpen(externalOpen);
        }
    }, [externalOpen]);

    const dialogSx = {
        zIndex: nested ? 9999 : undefined,
        '& .MuiDialog-paper': {
            zIndex: nested ? 9999 : undefined,
        },
        '& .MuiBackdrop-root': {
            zIndex: nested ? 9998 : undefined,
        },
        ...(customSx || {})
    };

    return (
        <React.Fragment>
            {triggerButton && React.cloneElement(triggerButton, {onClick: handleClickOpen})}
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth={maxWidth || 'sm'}
                container={nested ? document.body : undefined}
                disablePortal={false}
                sx={dialogSx}
                className={nested ? 'nested-dialog' : ''}
                {...otherProps}
            >
                {children ? (
                    // New approach: render children directly
                    children
                ) : (
                    // Original approach: render using props
                    <>
                        {(title || icon) && (
                            <DialogTitle>
                                <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                                    {icon && (
                                        <div style={IconContainer(theme.colors)}>
                                            {React.cloneElement(icon, {sx: {fontSize: "30px"}})}
                                        </div>
                                    )}
                                    <div>
                                        {title && (
                                            <Typography component="span" variant="h6" sx={DialogTitleText(theme.colors)}>
                                                {title}
                                            </Typography>
                                        )}
                                        {subtitle && <Typography sx={{...SubTitleScreen(theme.colors), fontSize: 15}}>
                                            {subtitle}
                                        </Typography>}
                                    </div>
                                </div>
                            </DialogTitle>
                        )}

                        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
                            {content}
                        </DialogContent>

                        <Divider/>

                        <DialogActions>
                            <Button onClick={handleClose} sx={!existAction ? PrimaryButton : SecondaryButton}>
                                {t('common.close')}
                            </Button>
                            {existAction && (
                                <Button
                                    onClick={actionFunction}
                                    sx={PrimaryButton}
                                    type={actionType}
                                    form={formId}
                                    disabled={isLoading || disableAction}
                                >
                                    {isLoading ? <CircularProgress size={24}/> : actionTitle}
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </React.Fragment>
    );
};

MuiDialog.propTypes = {
    triggerButton: PropTypes.element,
    icon: PropTypes.element,
    title: PropTypes.string,
    maxWidth: PropTypes.string,
    nested: PropTypes.bool,
    children: PropTypes.node,
    existAction: PropTypes.bool,
    actionTitle: PropTypes.string,
    actionFunction: PropTypes.func,
    onClose: PropTypes.bool,
    onExternalClose: PropTypes.func,
    actionType: PropTypes.string,
    formId: PropTypes.string,
    disableAction: PropTypes.bool,
    open: PropTypes.bool,
    isLoading: PropTypes.bool,
};

export default MuiDialog;